/**
 * Passwords — who can sign in, and getting the rest there.
 *
 * The headline is about staff: every member of staff without a password is
 * somebody who cannot get in to teach or mark until they use their link.
 *
 * Two deliberate omissions:
 *
 *   - **No "invite every trainee" button.** Trainees are still on TalentLMS at
 *     learn.learnica.academy. That email belongs to cutover day, and this
 *     page should not make it one click away by accident. Individual trainees
 *     can be sent a link, because somebody will ask.
 *   - **No password field.** Nobody sets a password for anybody else. The
 *     link goes to the account's own address and the person chooses.
 *
 * Sending to all staff confirms inline rather than with a browser dialog, so
 * the confirmation can name exactly who is about to be emailed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api';

type State = 'ready' | 'invited' | 'needs_link' | 'no_email';
type Person = {
  id: number; name: string; login: string; email: string | null;
  kind: 'staff' | 'trainee'; userType: string; state: State;
  lastSignedIn: string | null; linkExpires: string | null;
};
type Tally = Record<State, number> & { total: number };
type Readiness = {
  inviteDays: number; staff: Tally; trainees: Tally;
  allStaffCanSignIn: boolean; staffNotReady: number; people: Person[];
};
type Outcome = 'sent' | 'has_password' | 'no_email' | 'inactive' | 'outranks_you' | 'too_many_links' | 'not_found';
type InviteResult = { results: { id: number; outcome: Outcome }[]; counts: Partial<Record<Outcome, number>> };

const STATE_LABEL: Record<State, string> = {
  ready: 'has a password',
  invited: 'link sent',
  needs_link: 'needs a link',
  no_email: 'no email address',
};
const STATE_PILL: Record<State, string> = {
  ready: 'pill--completed',
  invited: 'pill--pending_review',
  needs_link: 'pill--incomplete',
  no_email: 'pill--failed',
};
/** Every reason is one somebody can act on, so each is said in full. */
const OUTCOME_WORDS: Record<Outcome, string> = {
  sent: 'sent',
  has_password: 'already have a password',
  no_email: 'have no email address on their account',
  inactive: 'are switched off',
  outranks_you: 'have permissions you do not have yourself',
  too_many_links: 'already hold three live links — ask them to look in their junk folder',
  not_found: 'no longer exist',
};

const day = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

function summarise(r: InviteResult): string {
  const sent = r.counts.sent ?? 0;
  const parts = [`${sent} link${sent === 1 ? '' : 's'} sent.`];
  for (const [outcome, n] of Object.entries(r.counts) as [Outcome, number][]) {
    if (outcome === 'sent' || !n) continue;
    parts.push(`${n} not sent: they ${OUTCOME_WORDS[outcome]}.`);
  }
  return parts.join(' ');
}

export function AdminSignInReadiness() {
  const [data, setData] = useState<Readiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [traineeFilter, setTraineeFilter] = useState('');

  const load = useCallback(async () => {
    try { setData(await api.get<Readiness>('/admin/sign-in')); setError(null); }
    catch (e) { setError((e as ApiError).message); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const send = async (body: object) => {
    setBusy(true); setNotice(null); setError(null);
    try {
      const r = await api.post<InviteResult>('/admin/sign-in/invite', body);
      setNotice(summarise(r));
      setConfirming(false);
      await load();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); }
  };

  const staff = useMemo(() => data?.people.filter((p) => p.kind === 'staff') ?? [], [data]);
  const trainees = useMemo(() => data?.people.filter((p) => p.kind === 'trainee') ?? [], [data]);
  const staffToInvite = staff.filter((p) => p.state === 'needs_link');
  const shownTrainees = useMemo(() => {
    const q = traineeFilter.trim().toLowerCase();
    if (!q) return [];
    return trainees.filter((p) =>
      [p.name, p.login, p.email ?? ''].some((v) => v.toLowerCase().includes(q))).slice(0, 50);
  }, [trainees, traineeFilter]);

  if (error && !data) return <p className="auth__error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;

  return (
    <>
      <h1 className="h1">Passwords</h1>

      {data.allStaffCanSignIn ? (
        <p className="notice">
          <strong>Every member of staff has a password of their own</strong> and can sign in.
        </p>
      ) : (
        <p className="notice notice--warn">
          <strong>{data.staffNotReady} member{data.staffNotReady === 1 ? '' : 's'} of staff
          {data.staffNotReady === 1 ? ' has' : ' have'} no password of their own</strong>
          {data.staff.invited > 0 && <> — {data.staff.invited} of them {data.staff.invited === 1 ? 'has' : 'have'} been
          sent a link and {data.staff.invited === 1 ? 'has' : 'have'} not used it yet</>}. Until they
          set one they cannot sign in. A link that has run out can be sent again from this page, and
          "I have forgotten my password" on the sign-in page works for them too.
        </p>
      )}

      {notice && <p className="notice" role="status">{notice}</p>}
      {error && <p className="auth__error" role="alert">{error}</p>}

      <section className="panel">
        <h2 className="h2">Staff</h2>
        <Tiles tally={data.staff} />

        {staffToInvite.length > 0 && !confirming && (
          <button className="btn btn--primary" disabled={busy} onClick={() => setConfirming(true)}>
            Send a link to the {staffToInvite.length} who need one
          </button>
        )}

        {confirming && (
          <div className="notice notice--warn">
            <p>
              This emails {staffToInvite.length} colleague{staffToInvite.length === 1 ? '' : 's'} a
              link to set their own password, valid for {data.inviteDays} days:
            </p>
            <p className="small">{staffToInvite.map((p) => p.name).join(', ')}</p>
            <div className="row">
              <button className="btn btn--primary" disabled={busy} onClick={() => void send({ scope: 'staff' })}>
                {busy ? 'Sending…' : `Yes, send ${staffToInvite.length} email${staffToInvite.length === 1 ? '' : 's'}`}
              </button>
              <button className="btn btn--ghost" disabled={busy} onClick={() => setConfirming(false)}>Cancel</button>
            </div>
          </div>
        )}

        {data.staff.no_email > 0 && (
          <p className="muted small">
            Anybody marked <em>no email address</em> cannot be sent anything. Add an address on their
            page under People, then send them a link here.
          </p>
        )}

        <PeopleTable people={staff} busy={busy} onSend={(id) => void send({ scope: 'people', userIds: [id] })} />
      </section>

      <section className="panel">
        <h2 className="h2">Trainees</h2>
        <Tiles tally={data.trainees} />
        <p className="muted small">
          Trainees came across from TalentLMS without passwords, because TalentLMS never exports them.
          There is no button to invite them all: they are still using TalentLMS, and that email belongs
          to cutover day. A trainee who asks for help can be found here and sent a link of their own.
        </p>
        <div className="filters">
          <input
            value={traineeFilter} onChange={(e) => setTraineeFilter(e.target.value)}
            placeholder="Find a trainee by name, username or email" aria-label="Find a trainee"
          />
        </div>
        {traineeFilter.trim() && (
          <PeopleTable people={shownTrainees} busy={busy}
            onSend={(id) => void send({ scope: 'people', userIds: [id] })} />
        )}
      </section>
    </>
  );
}

function Tiles({ tally }: { tally: Tally }) {
  return (
    <div className="tiles">
      <div className="tile"><span className="tile__value">{tally.ready}</span><span className="tile__label">have a password</span></div>
      <div className="tile"><span className="tile__value">{tally.invited}</span><span className="tile__label">link sent</span></div>
      <div className={`tile${tally.needs_link ? ' tile--warn' : ''}`}><span className="tile__value">{tally.needs_link}</span><span className="tile__label">need a link</span></div>
      <div className={`tile${tally.no_email ? ' tile--warn' : ''}`}><span className="tile__value">{tally.no_email}</span><span className="tile__label">no email address</span></div>
    </div>
  );
}

function PeopleTable({ people, busy, onSend }: { people: Person[]; busy: boolean; onSend: (id: number) => void }) {
  if (!people.length) return <p className="muted">Nobody matches.</p>;
  return (
    <table className="grid">
      <thead>
        <tr><th>Name</th><th>Email</th><th>Type</th><th>Password</th><th>Last signed in</th><th /></tr>
      </thead>
      <tbody>
        {people.map((p) => (
          <tr key={p.id}>
            <td>{p.name} <span className="muted small mono">{p.login}</span></td>
            <td className="small">{p.email ?? <span className="muted">none</span>}</td>
            <td className="small">{p.userType}</td>
            <td>
              <span className={`pill ${STATE_PILL[p.state]}`}>{STATE_LABEL[p.state]}</span>
              {p.state === 'invited' && p.linkExpires && (
                <span className="muted small"> until {day(p.linkExpires)}</span>
              )}
            </td>
            <td className="small">{day(p.lastSignedIn) || <span className="muted">never</span>}</td>
            <td>
              {(p.state === 'needs_link' || p.state === 'invited') && (
                <button className="btn btn--small btn--ghost" style={{ whiteSpace: 'nowrap' }}
                  disabled={busy} onClick={() => onSend(p.id)}>
                  {p.state === 'invited' ? 'Send another' : 'Send link'}
                </button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
